
import React, { useState, useRef } from 'react';
import { Camera, X, CheckCircle2 } from 'lucide-react';
import LoadingButton from '../src/components/ui/LoadingButton';

interface CompletionModalProps {
  onClose: () => void;
  onConfirm: (photoBase64: string) => Promise<void> | void;
}

const CompletionModal: React.FC<CompletionModalProps> = ({ onClose, onConfirm }) => {
  const [photo, setPhoto] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhoto(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    if (!photo || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await Promise.resolve(onConfirm(photo));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-slate-900/40 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-6 shadow-[0_2px_8px_rgba(0,0,0,0.04)] animate-in slide-in-from-bottom duration-300">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <div className="bg-indigo-50 p-2 rounded-xl">
              <Camera className="w-6 h-6 text-indigo-700" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-900">Photo Proof Required</h2>
              <p className="text-xs text-indigo-700 font-bold">Cannot complete without image</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-2 bg-slate-100 rounded-full text-slate-500 hover:bg-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          <div className="bg-white border border-slate-200 p-3 rounded-xl">
            <p className="text-slate-900 text-xs font-bold">
               Note: Upload a photo as proof of completion before marking this task as done.
            </p>
          </div>

          <div 
            onClick={() => fileInputRef.current?.click()}
            className={`w-full aspect-video rounded-2xl border-2 border-dashed flex flex-col items-center justify-center cursor-pointer transition-all overflow-hidden ${photo ? 'border-indigo-300 bg-indigo-50' : 'border-slate-200 bg-white hover:bg-slate-100'}`}
          >
            {photo ? (
              <div className="relative w-full h-full">
                <img src={photo} alt="Preview" className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 bg-indigo-900 text-white text-xs font-bold px-2 py-1 rounded-full">
                   Ready
                </div>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-3">
                <div className="p-4 bg-indigo-50 rounded-full text-indigo-700 animate-pulse">
                  <Camera className="w-8 h-8" />
                </div>
                <div className="text-center">
                  <p className="font-bold text-slate-700">Tap to Add Photo</p>
                  <p className="text-xs text-slate-400">Camera or Gallery</p>
                </div>
              </div>
            )}
            <input 
              type="file" 
              ref={fileInputRef}
              onChange={handleFileChange}
              accept="image/*"
              capture="environment"
              className="hidden"
            />
          </div>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 py-4 px-6 rounded-xl font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 active:scale-95 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <LoadingButton
              type="button"
              onClick={handleSubmit}
              isLoading={isSubmitting}
              loadingText="Completing..."
              variant="primary"
              disabled={!photo}
              className={`flex-[2] py-4 px-6 rounded-xl font-bold text-white flex items-center justify-center gap-2 active:scale-95 transition-all shadow-sm ${photo ? 'bg-indigo-900 hover:bg-indigo-800 shadow-[0_2px_8px_rgba(0,0,0,0.04)]' : 'bg-slate-300'}`}
            >
              <CheckCircle2 className="w-5 h-5" />
              {photo ? 'Complete Task' : 'Photo Required'}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompletionModal;


